import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  fetchProgramNewsItem,
  type ProgramNewsItem,
  type ProgramNewsItemDetailResponse,
} from "@/services/adminProgramNews";
import { currentAdminEmail } from "./adminEmail";
import ArticleDetail from "./ArticleDetail";
import { formatDate } from "./formatters";
import ReviewPanel from "./ReviewPanel";
import SourceArticleText from "./SourceArticleText";
import { formatSchoolName, splitTeamId } from "./teamLabel";
import { useReviewDetail } from "./useReviewDetail";

interface ArticleDetailSheetProps {
  item: ProgramNewsItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReviewChanged: () => void;
}

const ArticleDetailSheet = ({
  item,
  open,
  onOpenChange,
  onReviewChanged,
}: ArticleDetailSheetProps) => {
  const { data, refresh } = useReviewDetail<ProgramNewsItemDetailResponse>(open, () =>
    fetchProgramNewsItem(item._id)
  );
  const shown = data?.item ?? item;
  const { slug, gender } = splitTeamId(shown.teamId);

  const changed = async () => {
    await refresh();
    onReviewChanged();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-3xl">
        <SheetHeader className="text-left">
          <SheetTitle>{shown.title}</SheetTitle>
          <SheetDescription>
            {formatSchoolName(slug)}
            {gender ? ` · ${gender}` : ""} · Published {formatDate(shown.publishedAt)}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <ArticleDetail item={shown} />
          {!data ? (
            <p className="text-sm text-muted-foreground">Loading article text...</p>
          ) : (
            <SourceArticleText
              title={shown.title}
              sourceUrl={shown.sourceUrl}
              publishedAt={shown.publishedAt}
              text={shown.permittedText}
            />
          )}
          {data ? (
            <ReviewPanel
              targetType="item"
              targetId={shown._id}
              reviews={data.reviews}
              myEmail={currentAdminEmail()}
              suppressed={shown.processingStatus === "suppressed"}
              onChanged={changed}
            />
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ArticleDetailSheet;
